import { PrismicText } from '@prismicio/react';
import { fadeUpEffect } from '@publicss/utils';
import { Link } from 'gatsby';
import { gsap } from 'gsap';
import parse from 'html-react-parser';
import React from 'react';
import styled, { css } from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { usePageContext } from '../../../providers/PageContext';
import { media } from '../../../styles/media';
import src from '../../assets/image--blog-1.jpg';
import FacebookSVG from '../../assets/svg--facebook';
import LinkedInSVG from '../../assets/svg--linkedin';
import TelegramSVG from '../../assets/svg--telegram';
import TwitterSVG from '../../assets/svg--twitter';
import WhatsAppSVG from '../../assets/svg--whatsapp';
import { blogPath, getCurrURL } from '../path';

export const IntlBlogTypographyStyles = css`
  h1 {
    font-size: clamp(2.5rem, 5.2vw + 0.4rem, 3.75rem);
    font-weight: 500;
    line-height: 92%;
    letter-spacing: -0.01em;
  }
  h2 {
    font-size: clamp(1.5rem, 2.1vw + 0.7rem, 2rem);
    font-weight: 500;
    line-height: 110%;
    letter-spacing: -0.01em;
    &.normal {
      font-weight: normal;
    }
  }
  h3 {
    font-size: clamp(1.25rem, 1vw + 0.8rem, 1.5rem);
    font-weight: 500;
    line-height: 110%;
    letter-spacing: -0.01em;
  }
  h4 {
    font-size: 18px;
    font-weight: 500;
    line-height: 110%;
    letter-spacing: 0.01em;
    @media ${media.mdUp} {
      line-height: 130%;
    }
  }
  h5 {
    font-size: 16px;
    font-weight: 500;
    line-height: 125%;
    letter-spacing: 0.01em;
  }
  h6 {
    font-size: 16px;
    font-weight: 500;
    line-height: 125%;
    letter-spacing: 0.01em;
  }
  p {
    margin-bottom: 16px;
    line-height: 150%;
    letter-spacing: 0.01em;
    @media ${media.mdUp} {
      line-height: 160%;
    }
  }
`;

const Section = styled.section`
  ${IntlBlogTypographyStyles}

  opacity: 0;
  figure {
    margin-bottom: 60px;
  }
  ol,
  ul {
    list-style: initial;
  }
  .blog-article-body {
    a {
      text-decoration: underline;
      color: var(--primary);

      &:hover {
        color: var(--primary-dark);
      }
    }
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-bottom: 16px;
    }
    .wp-element-caption {
      margin: 16px 0;
      font-weight: 500;
      font-size: 10px;
      line-height: 140%;
      letter-spacing: 0.05em;
    }
  }
`;

const BlogPost = (props) => {
  const {
    siteTag,
    global: { entities_modal_heading, entities_modal_paragraph },
  } = usePageContext();

  React.useEffect(() => {
    const fadeElements = gsap.timeline();
    fadeElements.to('#block-hero-blog', {
      opacity: 1,
      delay: 0.25,
    });

    fadeUpEffect('#block-hero-blog .fade-up');
  }, []);

  const previousFeaturedImage =
    props.previous !== null && props.previous.featuredImage !== null
      ? {
          src: props.previous.featuredImage?.node?.localFile?.childImageSharp
            ?.fluid?.src,
        }
      : { src };

  const nextFeaturedImage =
    props.next !== null && props.next.featuredImage !== null
      ? {
          src: props.next.featuredImage?.node?.localFile?.childImageSharp?.fluid
            ?.src,
        }
      : { src };

  // const primary = props.data.edges[0].node.data;

  const currentURL = getCurrURL();

  return (
    <Section
      id="block-hero-blog"
      className="mt-10 overflow-hidden sm:mt-14 md:mt-28 lg:mb-20">
      <div className="pt-120 md:pt-140 lg:pt-190 container pb-20 md:pb-20 lg:pb-10">
        <div className="grid sm:grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          <div className="col-span-4 md:col-span-3 lg:col-span-5">
            <div className="eyebrow color--grey-4 fade-up mb-6">
              {props.post.date}
            </div>

            <h1 className="h2 color--primary fade-up">
              {parse(props.post.title)}
            </h1>

            <div className="fade-up my-12 flex items-center md-down:mt-6">
              <div className="mr-3 flex gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?url=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db">
                  <TwitterSVG width="16" height="16" fill="#777777" />
                </a>

                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db">
                  <FacebookSVG width="16" height="16" fill="#777777" />
                </a>

                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db">
                  <LinkedInSVG width="16" height="16" fill="#777777" />
                </a>

                <a
                  href={`https://t.me/share/url?url=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db">
                  <TelegramSVG width="16" height="16" fill="#777777" />
                </a>

                <a
                  href={`https://wa.me/?text=${currentURL}`}
                  target="_blank"
                  rel="noreferrer"
                  className="db">
                  <WhatsAppSVG width="16" height="16" fill="#777777" />
                </a>
              </div>

              <div className="border-l border-grey-3 pl-6">
                <Link
                  to={blogPath(siteTag)}
                  className="btn--rounded btn--rounded--slim btn--border btn--border--grey-3 color--grey-6 db"
                  direction="up"
                  duration="1"
                  bg="#E4E2D4">
                  View all
                </Link>
              </div>
            </div>
          </div>
          <div className="col-span-4 md:col-start-5 lg:col-span-6 lg:col-start-7">
            {!!props.post.content && (
              <section className="blog-article-body" itemProp="articleBody">
                {parse(props.post.content)}
              </section>
            )}
          </div>
          <div className="col-span-4 md:col-span-8 lg:col-span-12 lg:mb-40 lg:mt-40">
            <hr className="mb-5 mt-5" />
          </div>
          <div className="col-span-4 md:col-span-3 lg:col-span-4">
            <h2 className="h2 color--primary fade-up mb-8">
              <PrismicText field={entities_modal_heading.richText} />
            </h2>
          </div>
          <div className="col-span-4 md:col-start-5 lg:col-span-6 lg:col-start-7">
            <p className="mb-12">
              <PrismicText field={entities_modal_paragraph.richText} />
            </p>
            <div className="flex items-center">
              <Button href="#" className="btn btn--main color--white">
                Contact
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-20 grid grid-cols-4 gap-x-10 gap-y-12 md:grid-cols-12">
          {props.previous && (
            <div className="col-span-4 flex md:col-span-6">
              <Link
                to={blogPath(siteTag, props.previous.uri)}
                className="blog--card bg--white fade-up block flex-1 lg:flex lg:pt-12">
                <div className="blog--card--image flex-none">
                  <div
                    className="blog--card--bg"
                    style={{
                      backgroundImage: `url(${previousFeaturedImage.src})`,
                    }}
                  />
                </div>
                <div className="p-6 md:pl-12 md:pr-12 md:pt-8 lg:pb-6 lg:pl-8 lg:pr-14 lg:pt-0">
                  <div className="eyebrow color--grey-4 mb-6">
                    {props.previous.date}
                  </div>
                  <p className="h5 mb-6">{parse(props.previous.title)}</p>
                  <span className="btn--text--svg color--primary">
                    <span>{/** Learn More */}</span>
                    <svg
                      width="8"
                      height="8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path d="M.305.348l6.81.013.012 6.81H5.83l.05-4.608-4.848 4.85-.968-.968 4.85-4.85-4.608.052V.348z" />
                    </svg>
                  </span>
                </div>
              </Link>
            </div>
          )}
          {props.next && (
            <div className="col-span-4 flex md:col-span-6">
              <Link
                to={blogPath(siteTag, props.next.uri)}
                className="blog--card bg--white fade-up block flex-1 lg:flex lg:pt-12">
                <div className="blog--card--image flex-none">
                  <div
                    className="blog--card--bg"
                    style={{
                      backgroundImage: `url(${nextFeaturedImage.src})`,
                    }}
                  />
                </div>
                <div className="p-6 md:pl-12 md:pr-12 md:pt-8 lg:pb-6 lg:pl-8 lg:pr-14 lg:pt-0">
                  <div className="eyebrow color--grey-4 mb-6">
                    {props.next.date}
                  </div>
                  <p className="h5 mb-6">{parse(props.next.title)}</p>
                  <span className="btn--text--svg color--primary">
                    <span>{/** Learn More  */}</span>
                    <svg
                      width="8"
                      height="8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path d="M.305.348l6.81.013.012 6.81H5.83l.05-4.608-4.848 4.85-.968-.968 4.85-4.85-4.608.052V.348z" />
                    </svg>
                  </span>
                </div>
              </Link>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export { BlogPost };
